/* ==========================================================================
   Flight Box — adapter line-item lock.

   The Flight Box adds two cart lines: the unit and its region adapter, so the
   dropshipper's export carries both SKUs. This keeps the adapter tied to the
   unit so a customer can't end up with one and not the other:

     · adapter quantity always matches the unit quantity for the same plug
     · removing the adapter re-adds it while the unit is still there
     · removing the unit removes its adapter
     · the adapter's remove button and quantity stepper are hidden in the cart
     · checkout waits for the cart to be reconciled before it proceeds

   That last one matters because reconciliation is a round trip to the cart API.
   Change a quantity and click checkout fast enough and the navigation wins,
   sending the customer to checkout holding, say, 1 unit and 2 adapters. The
   guard below holds the click until the cart has settled.

   Loaded globally (the cart drawer opens on every page), so this lives in
   layout/theme.liquid rather than a section.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- config -----------------------------------------------------------
     Adapter variant ids, keyed by the parent's Plug Type option value. These
     must match the "Adapter variant per plug type" setting on the buy box.
     ---------------------------------------------------------------------- */
  var PARENT_HANDLE = 'flight-box';
  var ADAPTER_HANDLE = 'aircraft-vibes-flight-box-adapter';

  // The box is one plug-agnostic SKU, so plug type travels as a line item
  // property rather than a variant option. Written by the buy box; the old
  // variant-option reading is kept as a fallback for carts created before
  // the switch, which would otherwise be reconciled to zero adapters.
  var PLUG_PROPERTY = 'Plug type';

  // Never hold a customer at the checkout button longer than this. A cart that
  // won't settle is a bug on our side; making the sale unclickable is worse.
  var CHECKOUT_MAX_WAIT = 8000;

  // Shown on the checkout button while the cart is being brought up to date.
  var BUSY_LABEL = 'Updating cart…';

  // A settled cart releases in a couple of hundred milliseconds. Swapping the
  // label that briefly reads as a glitch, so the busy state only appears once
  // the wait is long enough to need explaining.
  var BUSY_LABEL_DELAY = 180;

  if (!window.fetch) return;

  /* Adapter variant ids are read from the product itself rather than hardcoded.
     A typed-in list goes stale silently — it already did once, when the adapter
     product was left in draft and every id 404'd. Reading them live means the
     mapping is whatever Shopify currently says it is. Cached per session, since
     this runs on every page that can open the cart drawer. */
  var ADAPTER_CACHE_KEY = 'fbAdapters:' + ADAPTER_HANDLE;
  var adaptersPromise = null;

  function loadAdapters() {
    if (adaptersPromise) return adaptersPromise;

    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(ADAPTER_CACHE_KEY)); } catch (e) {}
    if (cached) {
      adaptersPromise = Promise.resolve(cached);
      return adaptersPromise;
    }

    adaptersPromise = fetch('/products/' + ADAPTER_HANDLE + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        var map = {};
        if (product && product.variants) {
          product.variants.forEach(function (v) {
            var plug = String(v.option1 || v.title || '').trim().toLowerCase();
            if (plug) map[plug] = v.id;
          });
        }
        // Only cache a map with something in it. Caching an empty one — from a
        // 404, a draft product or a network blip — would poison the whole
        // session: reconcile bails when the map is empty, so an orphaned
        // adapter would sit in the cart uncorrected until the tab was closed.
        if (Object.keys(map).length) {
          try { sessionStorage.setItem(ADAPTER_CACHE_KEY, JSON.stringify(map)); } catch (e) {}
        } else {
          adaptersPromise = null; // let the next call retry
        }
        return map;
      })
      .catch(function () { adaptersPromise = null; return {}; });

    return adaptersPromise;
  }

  /* Plug type for a line: the property the buy box writes, falling back to the
     first variant option for carts predating the single-variant box. */
  function plugOf(item) {
    if (item.properties && item.properties[PLUG_PROPERTY]) {
      return item.properties[PLUG_PROPERTY];
    }
    if (item.options_with_values && item.options_with_values.length) {
      return item.options_with_values[0].value;
    }
    return (item.variant_options || [])[0];
  }

  /* ---- pending cart writes ----------------------------------------------
     Reconciling is only meaningful once the cart reflects what the customer
     just did. Themes debounce the quantity stepper, so at the instant someone
     clicks checkout the edit may not have been SENT yet — /cart.js then returns
     the pre-edit cart, which looks perfectly consistent, and the guard releases
     having fixed nothing. The edit lands moments later, after navigation.

     So we track two things: writes currently in flight, and whether a quantity
     control has been touched without its write having completed yet.
     ---------------------------------------------------------------------- */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var QUIET_MS = 150;        // let a follow-up write start before declaring calm
  var MAX_QUIET_WAIT = 5000; // never wait on a write that is never coming

  var inflight = 0;
  var pendingEdit = false;

  function noteWriteDone() {
    if (inflight > 0) inflight--;
    if (inflight === 0) pendingEdit = false;
  }

  // Wrap fetch so the theme's own cart calls are visible to us. Pass-through in
  // every respect — we only observe, and a throw here would break the cart.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var watched = method === 'POST' && CART_WRITE.test(url);
    if (watched) inflight++;
    var result = nativeFetch.apply(this, arguments);
    if (watched && result && result.then) {
      result.then(noteWriteDone, noteWriteDone);
    }
    return result;
  };

  // Some themes still use XHR for cart writes.
  var NativeXHR = window.XMLHttpRequest;
  if (NativeXHR && NativeXHR.prototype && NativeXHR.prototype.open) {
    var nativeOpen = NativeXHR.prototype.open;
    var nativeSend = NativeXHR.prototype.send;
    NativeXHR.prototype.open = function (method, url) {
      this.__fbWatched = String(method).toUpperCase() === 'POST' && CART_WRITE.test(String(url));
      return nativeOpen.apply(this, arguments);
    };
    NativeXHR.prototype.send = function () {
      if (this.__fbWatched) {
        inflight++;
        this.addEventListener('loadend', noteWriteDone);
      }
      return nativeSend.apply(this, arguments);
    };
  }

  /* Anything that edits a line. A click here means a write is coming, even if
     the theme hasn't sent it yet. */
  var EDIT_CONTROLS = [
    'button[name="minus"]', 'button[name="plus"]',
    'input[name="updates[]"]', 'input[name*="quantity" i]',
    '[class*="qty" i]', '[class*="quantity" i]',
    'cart-remove-button', '[class*="remove" i]',
    'a[href*="/cart/change"]',
    '[aria-label*="remove" i]', '[aria-label*="increase" i]', '[aria-label*="decrease" i]'
  ].join(',');

  function markEdit(e) {
    var path = (e.composedPath && e.composedPath()) || [e.target];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (n && n.nodeType === 1 && n.matches && n.matches(EDIT_CONTROLS)) {
        pendingEdit = true;
        return;
      }
    }
  }
  document.addEventListener('click', markEdit, true);
  document.addEventListener('change', markEdit, true);

  /* Resolves once nothing is in flight and no edit is outstanding. Bounded:
     a write that never arrives must not strand the customer. */
  function cartQuiet() {
    return new Promise(function (resolve) {
      var deadline = Date.now() + MAX_QUIET_WAIT;
      var calmSince = 0;
      (function poll() {
        if (Date.now() > deadline) return resolve();
        if (inflight === 0 && !pendingEdit) {
          if (!calmSince) calmSince = Date.now();
          if (Date.now() - calmSince >= QUIET_MS) return resolve();
        } else {
          calmSince = 0;
        }
        setTimeout(poll, 50);
      })();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  /* Returns, per adapter variant id, the quantity the cart should hold —
     the total units of the parent carrying that plug type. */
  function desiredAdapterQuantities(cart, adapters, ids) {
    var want = {};
    ids.forEach(function (id) { want[id] = 0; });

    cart.items.forEach(function (item) {
      if (item.handle !== PARENT_HANDLE) return;
      var plug = plugOf(item);
      if (!plug) return;
      var id = adapters[String(plug).trim().toLowerCase()];
      if (id) want[id] += item.quantity;
    });

    return want;
  }

  function currentAdapterQuantities(cart, ids) {
    var have = {};
    ids.forEach(function (id) { have[id] = 0; });
    cart.items.forEach(function (item) {
      if (ids.indexOf(item.variant_id) > -1) {
        have[item.variant_id] += item.quantity;
      }
    });
    return have;
  }

  function change(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: quantity })
    });
  }

  function add(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    });
  }

  function doReconcile() {
    return Promise.all([getCart(), loadAdapters()])
      .then(function (results) {
        var cart = results[0];
        var adapters = results[1];
        if (!cart || !cart.items) return false;

        // Whether anything in the cart justifies an adapter at all. Recorded
        // before the early return below, because the UI lock depends on it even
        // when there is nothing to reconcile.
        parentInCart = cart.items.some(function (item) {
          return item.handle === PARENT_HANDLE && item.quantity > 0;
        });

        var ids = Object.keys(adapters).map(function (k) { return adapters[k]; });
        // No adapters resolved means the product is missing, unpublished or the
        // fetch failed. Doing nothing is the safe response: reconciling against
        // an empty map would read every adapter as unwanted and strip the cart.
        if (!ids.length) return false;

        var want = desiredAdapterQuantities(cart, adapters, ids);
        var have = currentAdapterQuantities(cart, ids);
        var jobs = [];

        ids.forEach(function (id) {
          if (want[id] === have[id]) return;
          if (have[id] === 0 && want[id] > 0) {
            // Line is gone entirely — change.js can't resurrect it, so add.
            jobs.push(function () { return add(id, want[id]); });
          } else {
            // Covers both trimming to match and removing (quantity 0).
            jobs.push(function () { return change(id, want[id]); });
          }
        });

        if (!jobs.length) return false;

        // Sequential, not Promise.all. Concurrent writes to the same cart race
        // each other and the last response wins, which can silently undo a
        // sibling edit — the exact class of bug this whole file exists to stop.
        return jobs.reduce(function (chain, job) {
          return chain.then(job);
        }, Promise.resolve()).then(function () { return true; });
      })
      .catch(function () { return false; })
      .then(function (changed) {
        if (changed) {
          // Let the theme redraw with the corrected quantities.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        }
        return changed;
      });
  }

  /* A serial queue rather than a busy flag. A dropped call used to mean the
     last edit went unreconciled; now every call is honoured in order, and each
     caller gets a promise that resolves when ITS turn is finished. */
  var queue = Promise.resolve();
  function reconcile() {
    queue = queue.then(doReconcile, doReconcile);
    return queue;
  }

  /* Reconciles until a pass changes nothing, so the cart is provably settled
     rather than merely written to once. Capped — if it won't converge then
     something else is wrong, and looping forever helps nobody. */
  function settle(attempt) {
    attempt = attempt || 0;
    return reconcile().then(function (changed) {
      if (changed && attempt < 3) return settle(attempt + 1);
      return true;
    });
  }

  /* ---- hide the adapter's controls in the cart ---------------------------
     Walk up from the adapter's link to the first ancestor that actually holds
     quantity controls. That element is the row by definition — anything higher
     would have matched at its own level first — so this cannot bleed into a
     neighbouring line item the way a :has() selector does.
     ---------------------------------------------------------------------- */
  /* Used to FIND the row: the first ancestor holding one of these is the line
     item. Prices deliberately aren't in here — they sit closer to the title
     than the stepper does, so including them would stop the climb early, on a
     wrapper that holds the price but not the controls. */
  var ROW_MARKERS = [
    'quantity-input', 'quantity-selector', 'quantity-selector-component',
    'quantity-popover-component', 'cart-remove-button', 'cart-quantity-component',
    '[class*="quantity" i]', '[class*="qty" i]',
    '[class*="remove" i]', '[class*="delete" i]', '[class*="trash" i]',
    'input[name="updates[]"]', 'input[name*="quantity" i]', 'input[type="number"]',
    'button[name="minus"]', 'button[name="plus"]',
    'a[href*="/cart/change"]',
    '[aria-label*="remove" i]', '[aria-label*="delete" i]',
    '[aria-label*="quantity" i]', '[aria-label*="increase" i]', '[aria-label*="decrease" i]'
  ].join(',');

  /* Hidden once the row is found. The adapter is free and inseparable from the
     unit, so a 0.00 against it reads as a pricing bug rather than an inclusion
     — both the line total and the unit price go. */
  var HIDE_IN_ROW = ROW_MARKERS + ',' + [
    '[class*="price" i]', '[class*="money" i]', '[class*="cost" i]',
    '[data-price]', '.price', 'cart-line-price'
  ].join(',');

  /* True only while the cart holds a Flight Box. An adapter with no box is an
     orphan — it should never have survived reconciliation, but if it does, the
     customer must still be able to delete it. Hiding its remove button in that
     state turns a recoverable cart into one nothing but /cart/clear can fix. */
  var parentInCart = false;

  function lockRows() {
    var links = document.querySelectorAll('a[href*="' + ADAPTER_HANDLE + '"]');
    Array.prototype.forEach.call(links, function (link) {
      var row = link.parentElement;
      while (row && row !== document.body && !row.querySelector(ROW_MARKERS)) {
        row = row.parentElement;
      }
      if (!row || row === document.body) return;

      var found = row.querySelectorAll(HIDE_IN_ROW);
      Array.prototype.forEach.call(found, function (el) {
        if (parentInCart) {
          if (el.style.display !== 'none') {
            el.style.display = 'none';
            el.setAttribute('data-fb-hidden', '');
          }
        } else if (el.hasAttribute('data-fb-hidden')) {
          // Give the controls back rather than leaving them hidden by us.
          el.style.display = '';
          el.removeAttribute('data-fb-hidden');
        }
      });

      if (!parentInCart) {
        var stale = row.querySelector('[data-fb-locked-note]');
        if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
        return;
      }

      // Say why it can't be changed, so an empty space isn't just confusing.
      //
      // Only ever on the title link. A line item links to the product at least
      // twice — once wrapping the thumbnail, once wrapping the title — and the
      // two can resolve to different ancestors, so a per-row check alone still
      // let a second copy through under the image. The thumbnail link has no
      // text of its own, which is what tells them apart.
      if (!link.textContent.trim()) return;
      if (!row.querySelector('[data-fb-locked-note]')) {
        var note = document.createElement('div');
        note.setAttribute('data-fb-locked-note', '');
        note.textContent = 'Included with your Flight Box';
        note.style.cssText = 'font-size:12px;font-weight:600;opacity:.65;margin-top:4px;';
        link.parentNode.appendChild(note);
      }
    });
  }

  /* ---- hold checkout until the cart is settled ---------------------------
     Capture phase, so this runs before the theme's own handlers and before a
     link's default navigation. The click is replayed once the cart is correct,
     carrying a marker attribute so the replay passes straight through.
     ---------------------------------------------------------------------- */
  var CHECKOUT_TRIGGERS = [
    '[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    'a[href*="/checkouts/"]',
    'button[value*="checkout" i]',
    '[class*="checkout" i][type="submit"]',
    'button[type="submit"][class*="checkout" i]',
    '[id*="checkout" i][type="submit"]'
  ].join(',');

  /* composedPath() rather than closest(), because a button inside a web
     component's shadow root reports the HOST as e.target — closest() would then
     search the wrong tree and find nothing, letting the click through. The path
     includes shadow internals, so the real button is in there. */
  function triggerFrom(e) {
    var path = (e.composedPath && e.composedPath()) || [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches(CHECKOUT_TRIGGERS)) {
        return node;
      }
    }
    return e.target && e.target.closest ? e.target.closest(CHECKOUT_TRIGGERS) : null;
  }

  /* Rewrites a button's visible text without touching its structure.
     innerHTML would be simpler but replaces every child node, discarding the
     theme's own listeners and element references on a button we are about to
     click again. Editing text nodes in place leaves the elements themselves
     untouched, so the restore is exact. */
  function swapLabel(el, text) {
    var noop = function () {};
    if (!el || !document.createTreeWalker) return noop;

    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && node.nodeValue.trim()) nodes.push(node);
    }
    if (!nodes.length) return noop;

    var original = nodes.map(function (n) { return n.nodeValue; });

    // Keep the button from resizing mid-click, which looks worse than the wait.
    var width = el.offsetWidth;
    if (width) el.style.minWidth = width + 'px';

    nodes[0].nodeValue = text;
    for (var i = 1; i < nodes.length; i++) nodes[i].nodeValue = '';

    return function restore() {
      for (var j = 0; j < nodes.length; j++) nodes[j].nodeValue = original[j];
      el.style.minWidth = '';
    };
  }

  /* Holds an interaction open until the cart settles, then replays it. `replay`
     is whatever re-performs the original action once it's safe. */
  function hold(el, replay) {
    var busyEl = el && el.style ? el : null;
    var restoreLabel = null;
    var labelTimer = null;

    if (busyEl) {
      busyEl.setAttribute('aria-busy', 'true');
      busyEl.style.pointerEvents = 'none';
      labelTimer = setTimeout(function () {
        restoreLabel = swapLabel(busyEl, BUSY_LABEL);
        busyEl.style.opacity = '.7';
      }, BUSY_LABEL_DELAY);
    }

    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      if (busyEl) {
        clearTimeout(labelTimer);
        if (restoreLabel) restoreLabel();
        busyEl.removeAttribute('aria-busy');
        busyEl.style.pointerEvents = '';
        busyEl.style.opacity = '';
      }
      replay();
    };

    // The timeout is a safety valve, not the plan: whichever lands first wins,
    // and a stuck reconcile must never leave the button dead.
    var timer = setTimeout(release, CHECKOUT_MAX_WAIT);

    // Quiet BEFORE settle, not after. Reconciling against a cart that hasn't
    // received the customer's last edit yet finds nothing wrong and releases
    // immediately — which is precisely how the stale adapter quantity slipped
    // through to checkout.
    cartQuiet()
      .then(settle)
      .then(function () { clearTimeout(timer); release(); },
            function () { clearTimeout(timer); release(); });
  }

  function guardClick(e) {
    var btn = triggerFrom(e);
    if (!btn || btn.hasAttribute('data-fb-checkout-ready')) return;

    e.preventDefault();
    e.stopPropagation();

    hold(btn, function () {
      btn.setAttribute('data-fb-checkout-ready', '');
      btn.click();
    });
  }

  /* Some themes never fire a usable click — the cart is a <form> posting to
     /checkout, and Enter, a programmatic submit() or a button in a shadow root
     all reach submit without a click we can cancel. */
  function guardSubmit(e) {
    var form = e.target;
    if (!form || form.hasAttribute('data-fb-checkout-ready')) return;

    var action = form.getAttribute('action') || '';
    var isCheckout = action.indexOf('/checkout') > -1 ||
      (action.indexOf('/cart') > -1 && !!form.querySelector('[name="checkout"]'));
    if (!isCheckout) return;

    e.preventDefault();
    e.stopPropagation();

    hold(form.querySelector('[name="checkout"]') || null, function () {
      form.setAttribute('data-fb-checkout-ready', '');
      // submit() rather than requestSubmit(): the latter re-runs this handler
      // and every other submit listener, which we have already consumed.
      form.submit();
    });
  }

  document.addEventListener('click', guardClick, true);
  document.addEventListener('submit', guardSubmit, true);

  function run() {
    reconcile().then(lockRows);
  }

  // Initial pass, plus every signal the theme gives that the cart moved.
  document.addEventListener('DOMContentLoaded', run);
  ['on:cart:add', 'on:line-item:change', 'dispatch:cart-drawer:refresh', 'dispatch:cart-drawer:open']
    .forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(run, 120); });
    });

  // The drawer re-renders its contents on every change, wiping inline styles.
  var queuedPaint = false;
  new MutationObserver(function () {
    if (queuedPaint) return;
    queuedPaint = true;
    requestAnimationFrame(function () { queuedPaint = false; lockRows(); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState !== 'loading') run();
})();
